import { SidebarSimpleIcon } from "@phosphor-icons/react";
import { useNavigate } from "@tanstack/react-router";
import {
  type DefaultView,
  SavedViewsList,
} from "@/components/news-feed/saved-views-selector/SavedViewsList";
import { Button } from "@/components/ui/button";
import DateRangePicker from "@/components/ui/date-range-picker";
import type { SavedViewItem } from "@/data-access/saved-views/queries/saved-views-queries";
import { cn } from "@/lib/utils";
import { parseDateString } from "@/lib/utils/dateUtils";
import {
  getKeyMessageFilterExclusionFromFlatSavedView,
  type SavedViewFilter,
  savedViewFilterToSearchParams,
} from "@/lib/utils/filters";
import useKeyMessageStore from "@/store/news-feed/key-messages/key-message.slice";

const DEFAULT_FILTER_DAYS = 30;

export interface AnalysisContentManagementPanelProps {
  /** When true, filter sidebar is open. Used to hide Show filters button. */
  isFilterSidebarOpen?: boolean;
  /** Called when user clicks the Show/Hide filters button. */
  onToggleFilterSidebar?: () => void;
  /** Feed id from route (for saved view navigation). */
  feedId: string;
  /** Number of days for date range (from route search). */
  filterDays: number;
  /** Whether custom date range is active. */
  isCustomDaysRange: boolean;
  /** Start date YYYY-MM-DD (from route). */
  startDate: string;
  /** End date YYYY-MM-DD (from route). */
  endDate: string;
  /** Called when user applies a new date range in the picker. */
  onDateRangeApply: (range: {
    from?: Date;
    to?: Date;
    days?: number;
    isCustom?: boolean;
  }) => void;
}

const AnalysisContentManagementPanel = ({
  isFilterSidebarOpen = false,
  onToggleFilterSidebar,
  feedId,
  filterDays,
  isCustomDaysRange,
  startDate,
  endDate,
  onDateRangeApply,
}: AnalysisContentManagementPanelProps) => {
  const navigate = useNavigate();
  const updateKeyMessageFilterExclusion = useKeyMessageStore(
    (state) => state.updateSelectedKeyMessageFilterExclusion,
  );

  const handleViewSelect = (view: SavedViewItem | DefaultView) => {
    if (view.id === null) {
      updateKeyMessageFilterExclusion("OR");
      navigate({
        to: "/analysis/$feedId",
        params: { feedId },
        search: {
          filterDays: DEFAULT_FILTER_DAYS,
          period: "day",
          selected: { CATEGORY: ["NEWS"] },
          keyMessageConjunction: "OR",
          savedViewId: undefined,
        },
      });
    } else if ("feedArticleFilter" in view) {
      const keyMessageConjunction =
        getKeyMessageFilterExclusionFromFlatSavedView(view.feedArticleFilter);
      updateKeyMessageFilterExclusion(keyMessageConjunction);
      const searchParams = savedViewFilterToSearchParams(
        view.feedArticleFilter as SavedViewFilter,
      );
      navigate({
        to: "/analysis/$feedId",
        params: { feedId },
        search: {
          filterDays: searchParams.filterDays ?? DEFAULT_FILTER_DAYS,
          startDate: searchParams.startDate,
          endDate: searchParams.endDate,
          period: "day",
          selected: searchParams.selected ?? {},
          keyMessageConjunction,
          savedViewId: view.id,
        },
      });
    }
  };

  return (
    <div
      className={cn(
        "sticky top-0 z-10 bg-gray-50",
        "w-full overflow-x-auto overflow-y-visible",
      )}
    >
      <div className="@container w-full">
        <div className="grid grid-cols-1 items-start gap-3 px-3 py-4 @[700px]:grid-cols-[auto_1fr_auto] @[700px]:gap-6 @[700px]:px-6">
          <div
            className={cn(
              "min-w-0",
              isFilterSidebarOpen && "hidden @[700px]:block",
            )}
          >
            {!isFilterSidebarOpen && (
              <Button
                onPress={onToggleFilterSidebar}
                variant="outline"
                size="sm"
                aria-label="Show filters"
                className="flex items-center gap-1 rounded-3xl shadow-[0_0_25px_0_rgba(105,29,211,0.10)] text-xxs bg-white!"
              >
                <SidebarSimpleIcon className="w-4 h-4 text-gray-600" />
                Show filters
              </Button>
            )}
          </div>
          <div className="min-w-0 text-left">
            <SavedViewsList onViewSelect={handleViewSelect} />
          </div>
          <div className="flex min-w-0 justify-start @[700px]:justify-end">
            <DateRangePicker
              filterDays={filterDays}
              isCustomDaysRange={isCustomDaysRange}
              startDate={parseDateString(startDate)}
              endDate={parseDateString(endDate)}
              onApply={onDateRangeApply}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnalysisContentManagementPanel;
